import { useCallback, useState } from 'react';

export type FormErrors<T> = Partial<Record<keyof T, string>>;

export interface UseFormResult<T> {
  values: T;
  errors: FormErrors<T>;
  setField: <K extends keyof T>(key: K, value: T[K]) => void;
  setValues: (next: T) => void;
  setError: <K extends keyof T>(key: K, message: string | null) => void;
  setErrors: (next: FormErrors<T>) => void;
  reset: (next?: T) => void;
}

/**
 * Tiny controlled-form hook. Returns values + errors state with setters.
 * Deliberately framework-free — no validators, no schema plumbing. Callers
 * validate imperatively inside their submit handler and call `setError`.
 *
 * Setting a field clears that field's error automatically — a common UX
 * expectation and it keeps the callsites tidy.
 */
export function useForm<T extends object>(
  initial: T,
): UseFormResult<T> {
  const [values, setValuesState] = useState<T>(initial);
  const [errors, setErrorsState] = useState<FormErrors<T>>({});

  const setField = useCallback(
    <K extends keyof T>(key: K, value: T[K]) => {
      setValuesState((prev) => ({ ...prev, [key]: value }));
      setErrorsState((prev) => {
        if (prev[key] == null) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [],
  );

  const setValues = useCallback((next: T) => {
    setValuesState(next);
  }, []);

  const setError = useCallback(
    <K extends keyof T>(key: K, message: string | null) => {
      setErrorsState((prev) => {
        const next = { ...prev };
        if (message == null) {
          delete next[key];
        } else {
          next[key] = message;
        }
        return next;
      });
    },
    [],
  );

  const setErrors = useCallback((next: FormErrors<T>) => {
    setErrorsState(next);
  }, []);

  const reset = useCallback(
    (next?: T) => {
      setValuesState(next ?? initial);
      setErrorsState({});
    },
    [initial],
  );

  return { values, errors, setField, setValues, setError, setErrors, reset };
}
